/**
 * Data layer for `igaming-chargeback-deduction-from-affiliate-commission`. Thin typed fetch wrappers over /api/v1/igaming-chargeback-deduction-from-affiliate-commission.
 * Raw `fetch()` is confined to this file per the dash contract.
 */

export interface IgamingChargebackDeductionFromAffiliateCommissionRow {
	id: string;
	tenant_id: string;
	created_at: number;
	updated_at: number;
	[key: string]: unknown;
}

export interface ListIgamingChargebackDeductionFromAffiliateCommissionResponse {
	count: number;
	items: IgamingChargebackDeductionFromAffiliateCommissionRow[];
}

export interface CreateIgamingChargebackDeductionFromAffiliateCommissionBody {
	[key: string]: unknown;
}

const BASE = "/api/v1/igaming-chargeback-deduction-from-affiliate-commission";

async function parseOrThrow<T>(res: Response): Promise<T> {
	const text = await res.text();
	let data: unknown = null;
	if (text) {
		try {
			data = JSON.parse(text);
		} catch {
			data = text;
		}
	}
	if (!res.ok) {
		const msg =
			data &&
			typeof data === "object" &&
			data !== null &&
			"error" in data &&
			typeof (data as { error: unknown }).error === "string"
				? (data as { error: string }).error
				: `HTTP ${res.status}`;
		throw new Error(msg);
	}
	return data as T;
}

export async function list(): Promise<ListIgamingChargebackDeductionFromAffiliateCommissionResponse> {
	const res = await fetch(BASE, {
		method: "GET",
		credentials: "include",
		headers: { accept: "application/json" },
	});
	return parseOrThrow<ListIgamingChargebackDeductionFromAffiliateCommissionResponse>(res);
}

export async function create(body: CreateIgamingChargebackDeductionFromAffiliateCommissionBody): Promise<IgamingChargebackDeductionFromAffiliateCommissionRow> {
	const res = await fetch(BASE, {
		method: "POST",
		credentials: "include",
		headers: {
			accept: "application/json",
			"content-type": "application/json",
		},
		body: JSON.stringify(body ?? {}),
	});
	return parseOrThrow<IgamingChargebackDeductionFromAffiliateCommissionRow>(res);
}

export async function remove(id: string): Promise<{ removed: boolean }> {
	const res = await fetch(`${BASE}/${encodeURIComponent(id)}`, {
		method: "DELETE",
		credentials: "include",
		headers: { accept: "application/json" },
	});
	return parseOrThrow<{ removed: boolean }>(res);
}

export const igamingChargebackDeductionFromAffiliateCommissionQueryKeys = {
	all: ["igaming-chargeback-deduction-from-affiliate-commission"] as const,
	list: () => [...igamingChargebackDeductionFromAffiliateCommissionQueryKeys.all] as const,
	detail: (id: string) => [...igamingChargebackDeductionFromAffiliateCommissionQueryKeys.all, id] as const,
};
